import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { MouseEventHandler } from "react";
import clsx from "clsx";
import { colorSystem } from "../../theme";
import { Pencil, Settings } from "lucide-react";
import { ReactComponent as ArrowIcon } from "../../icons/arrowGray.svg";

const useStyles = makeStyles((theme) => ({
  header: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "top",
    position: "relative",
    marginBottom: theme.spacing(4),
  },

  collapsedHeader: {
    marginBottom: 0,
  },

  headerIcon: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },

  title: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: "29px",
    color: theme.palette.common.black,
  },

  subtitle: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: "20px",
    color: theme.palette.text.secondary,
  },

  todayContainer: {
    display: "flex",
    alignItems: "center",
  },

  today: {
    fontSize: 10,
    fontWeight: 800,
    lineHeight: "12px",
    color: theme.palette.common.black,
    backgroundColor: colorSystem.gray9,
    borderRadius: 4,
    width: "fit-content",
    padding: theme.spacing(0.2, 0.8),
    textTransform: "uppercase",
  },

  todayValue: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: "24px",
    color: theme.palette.common.black,
  },

  todayTitle: {
    fontSize: 13,
    lineHeight: "13px",
    fontWeight: 500,
    color: colorSystem.gray,
    marginLeft: 6,
  },

  iconButtonGroup: {
    display: "flex",
    alignItems: "center",
    position: "absolute",
    top: theme.spacing(2),
    right: 0,
  },

  iconButton: {
    margin: theme.spacing(-10, 0),
  },

  arrowIcon: {
    marginLeft: theme.spacing(2),
  },

  arrowIconDown: {
    transform: "rotate(180deg)",
  },

  arrowIconRight: {
    transform: "rotate(90deg)",
    marginRight: theme.spacing(1),
  },
}));

interface ClientDetailsCardHeaderProps {
  icon: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  todayValue?: string;
  onEditClick?: MouseEventHandler;
  disableOnEdit?: boolean;
  onSettingsClick?: MouseEventHandler;
  disableSettings?: boolean;
  disableCompactEdit?: boolean;
  isOpenContent?: boolean;
  setIsOpenContent?: (value: ((val: boolean) => boolean)) => void;
  children?: React.ReactNode;
}

export default function ClientDetailsCardHeader(
  props: ClientDetailsCardHeaderProps,
) {
  const {
    icon,
    title,
    subtitle,
    todayValue,
    onEditClick,
    onSettingsClick,
    disableOnEdit,
    disableSettings,
    disableCompactEdit,
    isOpenContent,
    setIsOpenContent,
    children,
  } = props;

  const s = useStyles();
  const smUp = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  const canShowCompactEdit =
    onEditClick && !onSettingsClick && !setIsOpenContent && !smUp && !disableCompactEdit;

  return (
    <Box
      className={clsx(
        s.header,
        setIsOpenContent && !isOpenContent && s.collapsedHeader,
      )}
      onClick={(e) =>
        canShowCompactEdit
          ? onEditClick(e)
          : !smUp && setIsOpenContent?.((open) => !open)
      }
    >
      <Box className={s.headerIcon}>
        {icon}
        {todayValue ? (
          <Box>
            <Box className={s.todayContainer}>
              <Typography variant="h6" className={s.today} children="Today" />
              <Typography
                variant="h6"
                className={s.todayTitle}
                children={title}
              />
            </Box>

            <Typography variant="body1" className={s.todayValue}>
              {todayValue}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" className={s.title} children={title} />
            <Typography
              variant="body1"
              className={s.subtitle}
              children={subtitle}
            />
          </Box>
        )}

        {children}

        <Box className={s.iconButtonGroup}>
          {canShowCompactEdit ? (
            <ArrowIcon
              className={clsx(s.arrowIcon, s.arrowIconRight)}
              onClick={(e) => {
                onEditClick(e);
                e.stopPropagation();
              }}
            />
          ) : (
            onEditClick && (
              <IconButton
                disabled={disableOnEdit}
                className={s.iconButton}
                onClick={(e) => {
                  onEditClick(e);
                  e.stopPropagation();
                }}
              >
                <Pencil size={18} />
              </IconButton>
            )
          )}
          {onSettingsClick && (
            <IconButton
              disabled={disableSettings}
              onClick={(e) => {
                onSettingsClick(e);
                e.stopPropagation();
              }}
              className={s.iconButton}
            >
              <Settings size={18} />
            </IconButton>
          )}
          {setIsOpenContent && !smUp && (
            <ArrowIcon
              className={clsx(s.arrowIcon, !isOpenContent && s.arrowIconDown)}
              onClick={(e) => {setIsOpenContent(open => !open); e.stopPropagation();}}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
